import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api/client";
import { Card, Badge, Spinner, EmptyState } from "@/components/ui";
import Button from "@/components/ui/Button";
import Modal from "@/components/ui/Modal";
import { useAuthStore } from "@/store/authStore";
import CoinBurn, { useCoinBurnTrigger } from "@/components/student/CoinBurn";
import { sfxPurchase, sfxClick } from "@/utils/soundEngine";
import toast from "react-hot-toast";
import clsx from "clsx";

/* ─── Rarity config ─────────────────────────────────────────────────────────── */
const RARITY_META = {
  common:    { label: "Common",    color: "#9ca3af", glow: "rgba(156,163,175,0.3)",  bg: "rgba(156,163,175,0.05)", border: "rgba(156,163,175,0.3)" },
  rare:      { label: "Rare",      color: "#3b82f6", glow: "rgba(59,130,246,0.4)",   bg: "rgba(59,130,246,0.06)",  border: "rgba(59,130,246,0.55)" },
  epic:      { label: "Epic",      color: "#8b5cf6", glow: "rgba(139,92,246,0.45)",  bg: "rgba(139,92,246,0.07)",  border: "rgba(139,92,246,0.65)" },
  legendary: { label: "Legendary", color: "#fbbf24", glow: "rgba(251,191,36,0.55)",  bg: "rgba(251,191,36,0.06)",  border: "rgba(251,191,36,0.75)" },
};

const TYPE_FILTER_ITEMS = [
  { id: "all",     label: "Alles"   },
  { id: "digital", label: "Digital" },
  { id: "irl",     label: "Versand" },
  { id: "mixed",   label: "Mixed"   },
];

/* ─── Belohnungszentrum (matches hacked.studox.eu's reward-center flavor) ───── */
const RZ_EFFECT_IDS  = ["si-rz-confetti", "si-rz-glow", "si-rz-rainbow"];
const RZ_UPGRADE_IDS = ["si-rz-focus", "si-rz-xpboost", "si-rz-streakshield"];

function CoinIcon() {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" stroke="#fbbf24" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v12M9 9h4.5a1.5 1.5 0 0 1 0 3H9m0 3h5.5a1.5 1.5 0 0 0 0-3"/></svg>;
}

function CoinAmount({ value, className = "" }) {
  return (
    <span className={clsx("flex items-center gap-1 font-black", className)}>
      <CoinIcon />
      {value.toLocaleString("de-DE")}
    </span>
  );
}

/* ─── Holographic item card ─────────────────────────────────────────────────── */
function ItemCard({ item, coins, onSelect, purchasedCount = 0 }) {
  const canAfford   = coins >= item.cost;
  const meta        = RARITY_META[item.rarity] || RARITY_META.common;
  const isLegendary = item.rarity === "legendary";
  const maxReached  = item.maxPerUser != null && purchasedCount >= item.maxPerUser;
  const remaining   = item.maxPerUser != null ? item.maxPerUser - purchasedCount : null;
  const isDisabled  = maxReached;

  return (
    <button
      onClick={() => { if (isDisabled) return; sfxClick(); onSelect(item); }}
      disabled={isDisabled}
      className={clsx(
        "relative flex flex-col items-center p-4 rounded-2xl border-2 text-left",
        "transition-all duration-300 group overflow-hidden",
        isDisabled
          ? "opacity-40 cursor-not-allowed"
          : "hover:-translate-y-1 hover:scale-[1.03] active:scale-[0.97]",
        !canAfford && !isDisabled && "opacity-55",
      )}
      style={{
        borderColor: meta.border,
        background:  meta.bg,
        boxShadow:   isLegendary
          ? `0 4px 20px ${meta.glow}, 0 0 0 1px rgba(255,255,255,0.03) inset`
          : `0 2px 10px rgba(0,0,0,0.3)`,
        animation: isLegendary ? "legendary-pulse 2.5s ease-in-out infinite" : undefined,
      }}
    >
      {/* Holographic sheen on hover */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none holographic"
        style={{ borderRadius: "14px" }}
      />

      {/* Top shine */}
      <div
        className="absolute inset-x-0 top-0 h-px pointer-events-none"
        style={{ background: `linear-gradient(90deg, transparent, ${meta.color}40, transparent)` }}
      />

      {/* Max reached overlay */}
      {maxReached && (
        <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none"
          style={{ background: "rgba(0,0,0,0.55)", borderRadius: "14px" }}>
          <div className="flex flex-col items-center gap-1">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="#22c55e" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            <span className="text-[9px] font-black text-green-400 uppercase tracking-wide">
              {item.maxPerUser === 1 ? "Bereits erworben" : `${purchasedCount}/${item.maxPerUser}× erworben`}
            </span>
          </div>
        </div>
      )}

      {/* Rarity corner badge */}
      <div
        className="absolute top-2 right-2 text-[8px] font-black px-1.5 py-0.5 rounded-md"
        style={{ color: meta.color, background: `${meta.color}18`, border: `1px solid ${meta.color}35` }}
      >
        {meta.label.toUpperCase()}
      </div>

      {/* Max per user badge */}
      {item.maxPerUser != null && !maxReached && (
        <div className="absolute top-2 left-2 text-[7px] font-black px-1.5 py-0.5 rounded-md"
          style={{ color: "#94a3b8", background: "rgba(148,163,184,0.1)", border: "1px solid rgba(148,163,184,0.25)" }}>
          {remaining === 1 ? "Letzte Chance" : `Noch ${remaining}×`}
        </div>
      )}

      {/* Emoji with glow */}
      <div
        className="text-4xl mb-2.5 mt-1 transition-transform duration-300 group-hover:scale-110"
        style={{ filter: `drop-shadow(0 0 12px ${meta.glow})` }}
      >
        {item.emoji}
      </div>

      <p className="font-black text-sm text-center leading-tight w-full line-clamp-2">{item.name}</p>

      {item.type === "irl" && (
        <span className="mt-1.5 text-[9px] px-1.5 py-0.5 rounded-md bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
          📦 Versand
        </span>
      )}

      <div className="mt-3 w-full flex items-center justify-center">
        <CoinAmount
          value={item.cost}
          className={clsx("text-sm", canAfford ? "text-yellow-400" : "text-red-400")}
        />
      </div>

      {!canAfford && (
        <p className="text-[9px] text-red-400 mt-1 text-center">
          noch {(item.cost - coins).toLocaleString("de-DE")} fehlen
        </p>
      )}
    </button>
  );
}

/* ─── Belohnungszentrum tab ──────────────────────────────────────────────────
   Mirrors hacked.studox.eu's reward-center flavor inside the real Flow shop:
   Effekte + Upgrades are REAL shop items (same secure /shop/buy flow as the
   main shop). Träume are big, real-life rewards a student REQUESTS with coins —
   coins are only ever deducted once a teacher/admin approves (see admin.js). */
function BelohnungszentrumTab({ rzEffects, rzUpgrades, dreams, coins, purchasedCounts, onSelectItem, dreamStatus, onRequestDream, requestingDream }) {
  return (
    <div className="space-y-7 animate-fade-in">
      <div className="rounded-2xl border border-violet-500/25 bg-gradient-to-br from-violet-950/40 via-nexus-sf to-cyan-950/20 px-4 py-3.5">
        <p className="text-sm font-black text-violet-300">✦ Belohnungszentrum</p>
        <p className="text-[11px] text-gray-400 mt-0.5">Dieselben Belohnungen wie im großen Finale von hacked.studox.eu — jetzt hier für dich.</p>
      </div>

      <RzSection title="✦ Effekte" sub="Sofort-Belohnungen aus reinem Licht" color="#22e3ff">
        {rzEffects.map((item) => (
          <ItemCard key={item.id} item={item} coins={coins} onSelect={onSelectItem} purchasedCount={purchasedCounts[item.id] || 0} />
        ))}
      </RzSection>

      <RzSection title="⚙️ Upgrades" sub="Werde dauerhaft stärker" color="#39ff14">
        {rzUpgrades.map((item) => (
          <ItemCard key={item.id} item={item} coins={coins} onSelect={onSelectItem} purchasedCount={purchasedCounts[item.id] || 0} />
        ))}
      </RzSection>

      <RzSection title="🌙 Träume" sub="Große Belohnungen — deine Lehrkraft entscheidet über die Freigabe" color="#ff7ad9">
        {dreams.map((d) => (
          <DreamCard key={d.id} dream={d} coins={coins} status={dreamStatus(d.id)} onRequest={() => onRequestDream(d.id)} requesting={requestingDream} />
        ))}
      </RzSection>
    </div>
  );
}

function RzSection({ title, sub, color, children }) {
  return (
    <section>
      <div className="mb-2.5 flex items-baseline gap-2">
        <h3 className="text-sm font-black" style={{ color }}>{title}</h3>
        <span className="text-[11px] text-gray-500">{sub}</span>
      </div>
      <div className="grid grid-cols-2 gap-3">{children}</div>
    </section>
  );
}

function DreamCard({ dream, coins, status, onRequest, requesting }) {
  const canAfford = coins >= dream.cost;
  const pending  = status?.status === "pending";
  const approved = status?.status === "approved";
  const denied   = status?.status === "denied";

  return (
    <div className="relative flex flex-col items-center gap-1.5 rounded-2xl border p-4 text-center overflow-hidden"
      style={{ borderColor: "rgba(255,122,217,0.3)", background: "rgba(255,122,217,0.04)" }}>
      <span className="text-3xl" style={{ filter: !canAfford && !approved ? "grayscale(0.4) brightness(0.85)" : "none" }}>{dream.emoji}</span>
      <span className="text-[0.78rem] font-bold leading-tight text-white">{dream.name}</span>
      <p className="text-[10px] text-gray-500 leading-snug">{dream.desc}</p>

      {approved ? (
        <span className="mt-1 rounded-full bg-green-500/15 border border-green-500/40 px-2.5 py-1 text-[10px] font-black uppercase text-green-400">✓ Genehmigt — sprich mit deiner Lehrkraft</span>
      ) : pending ? (
        <span className="mt-1 rounded-full bg-amber-500/15 border border-amber-500/40 px-2.5 py-1 text-[10px] font-black uppercase text-amber-400">⏳ Wartet auf Lehrkraft</span>
      ) : (
        <>
          <span className="mt-1 rounded-full px-2.5 py-0.5 text-[0.6rem] font-bold uppercase tracking-wide" style={{ color: "#ff7ad9", background: "rgba(255,122,217,0.1)" }}>
            <CoinIcon /> {dream.cost.toLocaleString("de-DE")}
          </span>
          <button
            onClick={onRequest}
            disabled={!canAfford || requesting}
            className={clsx(
              "mt-1.5 w-full rounded-xl py-1.5 text-[11px] font-black transition-all active:scale-95",
              canAfford ? "bg-pink-600/80 text-white hover:bg-pink-600" : "bg-nexus-s2 text-gray-600 cursor-not-allowed"
            )}
          >
            {canAfford ? "Anfragen" : `Noch ${(dream.cost - coins).toLocaleString("de-DE")} fehlen`}
          </button>
          {denied && <p className="text-[9px] text-red-400 mt-1">{status.note || "Abgelehnt — frag später erneut."}</p>}
        </>
      )}
    </div>
  );
}

export default function Shop() {
  const { user } = useAuthStore();
  const [typeFilter,   setTypeFilter]   = useState("all");
  const [selectedItem, setSelectedItem] = useState(null);
  const [tab,          setTab]          = useState("shop");
  const [burnOpen,     setBurnOpen]     = useState(false);
  const qc = useQueryClient();

  const coins       = user?.studentProfile?.coins || 0;
  const burnTrigger = useCoinBurnTrigger();

  const { data: itemsData, isLoading } = useQuery({
    queryKey: ["shop-items", typeFilter],
    queryFn:  () => api.get(`/shop/items${typeFilter !== "all" ? `?type=${typeFilter}` : ""}`).then((r) => r.data),
  });

  const { data: inventoryData } = useQuery({
    queryKey: ["inventory"],
    queryFn:  () => api.get("/shop/inventory").then((r) => r.data),
  });

  // Belohnungszentrum: fetched once, independent of the "shop" tab's type filter
  const { data: rzItemsData } = useQuery({
    queryKey: ["shop-items-rz"],
    queryFn:  () => api.get("/shop/items").then((r) => r.data),
    enabled:  tab === "belohnung",
  });
  const { data: dreamsData } = useQuery({
    queryKey: ["dreams-catalog"],
    queryFn:  () => api.get("/shop/dreams").then((r) => r.data),
    enabled:  tab === "belohnung",
  });
  const { data: myDreamsData } = useQuery({
    queryKey: ["dreams-mine"],
    queryFn:  () => api.get("/shop/dreams/mine").then((r) => r.data),
    enabled:  tab === "belohnung",
  });

  // Build a count map: itemId → how many times purchased
  const purchasedCounts = {};
  for (const inv of inventoryData?.inventory || []) {
    purchasedCounts[inv.itemId] = (purchasedCounts[inv.itemId] || 0) + 1;
  }

  const { refreshMe } = useAuthStore();

  const buy = useMutation({
    mutationFn: (itemId) => api.post("/shop/buy", { itemId }),
    onSuccess: (res) => {
      sfxPurchase();
      toast.success(`${res.data.item.emoji} ${res.data.item.name} gekauft!`);
      refreshMe();
      qc.invalidateQueries({ queryKey: ["shop-items"] });
      qc.invalidateQueries({ queryKey: ["inventory"] });
      qc.invalidateQueries({ queryKey: ["student", "me"] });
      setSelectedItem(null);
    },
    onError: (err) => toast.error(err.response?.data?.error || "Kauf fehlgeschlagen"),
  });

  const requestDream = useMutation({
    mutationFn: (dreamId) => api.post("/shop/dreams/request", { dreamId }),
    onSuccess: (res) => {
      sfxPurchase();
      toast.success(`✦ Traum angefragt: ${res.data.request.dreamName} — deine Lehrkraft entscheidet.`);
      qc.invalidateQueries({ queryKey: ["dreams-mine"] });
    },
    onError: (err) => toast.error(err.response?.data?.error || "Anfrage fehlgeschlagen"),
  });

  const items     = itemsData?.items || [];
  const inventory = inventoryData?.inventory || [];

  const rzItems    = rzItemsData?.items || [];
  const rzEffects  = RZ_EFFECT_IDS.map((id) => rzItems.find((i) => i.id === id)).filter(Boolean);
  const rzUpgrades = RZ_UPGRADE_IDS.map((id) => rzItems.find((i) => i.id === id)).filter(Boolean);
  const dreams     = dreamsData?.dreams || [];
  const myDreams    = myDreamsData?.requests || [];
  const dreamStatus = (dreamId) => {
    const mine = myDreams.filter((r) => r.dreamId === dreamId);
    // a pending or approved request wins over an older denied one
    return mine.find((r) => r.status === "pending") || mine.find((r) => r.status === "approved") || mine[0] || null;
  };

  if (isLoading) return (
    <div className="flex justify-center pt-20">
      <div className="relative w-12 h-12">
        <div className="absolute inset-0 rounded-full border-2 border-t-violet-500 border-r-cyan-500 border-b-pink-500 border-l-transparent animate-spin" />
      </div>
    </div>
  );

  return (
    <div className="space-y-4 animate-fade-in">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-black flex items-center gap-2">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" stroke="#8b5cf6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/>
            </svg>
            StudoX Shop
          </h1>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="text-[11px] text-gray-500">Guthaben:</span>
            <CoinAmount value={coins} className="text-sm text-yellow-400" />
          </div>
        </div>

        <div className="scrollbar-hide flex gap-1 overflow-x-auto">
          {[
            { id: "shop",       label: "Shop",      icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg> },
            { id: "belohnung",  label: "Belohnung", icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l2.4 5.5L20 8.3l-4 4 1 5.7L12 15.8 7 18l1-5.7-4-4 5.6-.8z"/></svg> },
            { id: "inventory",  label: "Inventar",  icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg> },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => { sfxClick(); setTab(t.id); }}
              className={clsx(
                "flex shrink-0 items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border transition-all duration-200",
                t.id === tab
                  ? "bg-violet-600 border-violet-500 text-white shadow-flow"
                  : "bg-nexus-s2 border-nexus-border text-gray-400 hover:border-violet-500/30"
              )}
            >
              {t.icon}{t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Coin burn banner ───────────────────────────────────────────────── */}
      {burnTrigger && tab === "shop" && (
        <button
          onClick={() => setBurnOpen(true)}
          className="w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl border border-orange-700/50 hover:border-orange-600/70 transition-all group overflow-hidden relative"
          style={{
            background: "linear-gradient(135deg, rgba(249,115,22,0.12) 0%, rgba(239,68,68,0.08) 100%)",
            animation: "burn-glow 2s ease-in-out infinite",
          }}
        >
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: "linear-gradient(135deg, rgba(249,115,22,0.08) 0%, transparent 60%)" }} />
          <span className="text-2xl flex-shrink-0" style={{ filter: "drop-shadow(0 0 8px #f97316)" }}>🔥</span>
          <div className="flex-1 text-left">
            <p className="text-sm font-black text-orange-300">Coin-Verbrennung verfügbar</p>
            <p className="text-[11px] text-orange-400/60">Verbrenne Coins für exklusive Belohnungen</p>
          </div>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="#f97316" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 group-hover:translate-x-0.5 transition-transform"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
        </button>
      )}

      {tab === "shop" && (
        <>
          {/* Type filter pills */}
          <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-0.5">
            {TYPE_FILTER_ITEMS.map((t) => (
              <button
                key={t.id}
                onClick={() => { sfxClick(); setTypeFilter(t.id); }}
                className={clsx(
                  "flex-shrink-0 px-4 py-1.5 rounded-xl text-xs font-bold border transition-all duration-200",
                  t.id === typeFilter
                    ? "bg-violet-600 border-violet-500 text-white shadow-flow"
                    : "bg-nexus-sf border-nexus-border text-gray-500 hover:text-gray-300 hover:border-violet-500/30"
                )}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Items grid */}
          {items.length === 0 ? (
            <EmptyState
              icon={<svg width="32" height="32" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" stroke="#4b5563" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/></svg>}
              title="Noch keine Artikel"
              desc="Der Admin fügt bald Items hinzu"
            />
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {items.map((item) => (
                <ItemCard key={item.id} item={item} coins={coins} onSelect={setSelectedItem} purchasedCount={purchasedCounts[item.id] || 0} />
              ))}
            </div>
          )}

          {/* Soft burn CTA */}
          {coins > 0 && !burnTrigger && (
            <button
              onClick={() => setBurnOpen(true)}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border border-nexus-border bg-nexus-sf hover:border-orange-700/40 hover:bg-orange-950/20 transition-all text-xs text-gray-600 hover:text-orange-400"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2c0 0-5.5 5.5-5.5 11a5.5 5.5 0 0 0 11 0C17.5 7.5 12 2 12 2z"/></svg>
              Coins verbrennen → Belohnungen
            </button>
          )}
        </>
      )}

      {tab === "belohnung" && (
        <BelohnungszentrumTab
          rzEffects={rzEffects}
          rzUpgrades={rzUpgrades}
          dreams={dreams}
          coins={coins}
          purchasedCounts={purchasedCounts}
          onSelectItem={setSelectedItem}
          dreamStatus={dreamStatus}
          onRequestDream={(id) => requestDream.mutate(id)}
          requestingDream={requestDream.isPending}
        />
      )}

      {tab === "inventory" && (
        /* Inventory */
        inventory.length === 0 ? (
          <EmptyState
            icon={<svg width="32" height="32" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" stroke="#4b5563" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>}
            title="Inventar leer"
            desc="Kaufe Items im Shop!"
            action={<Button onClick={() => setTab("shop")}>Zum Shop</Button>}
          />
        ) : (
          <div className="space-y-2.5">
            {inventory.map((inv) => {
              const meta = RARITY_META[inv.item.rarity] || RARITY_META.common;
              return (
                <div
                  key={inv.id}
                  className="flex items-center gap-3 p-4 rounded-2xl border-2 relative overflow-hidden"
                  style={{ borderColor: meta.border, background: meta.bg }}
                >
                  <div className="absolute inset-x-0 top-0 h-px pointer-events-none" style={{ background: `linear-gradient(90deg, transparent, ${meta.color}30, transparent)` }} />
                  <span className="text-3xl flex-shrink-0" style={{ filter: `drop-shadow(0 0 8px ${meta.glow})` }}>{inv.item.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-sm">{inv.item.name}</p>
                    <p className="text-[11px] text-gray-500 truncate mt-0.5">{inv.item.description}</p>
                    <div className="flex gap-1.5 mt-1.5">
                      <span className="text-[10px] px-2 py-0.5 rounded-full border font-bold" style={{ color: meta.color, borderColor: `${meta.color}40`, background: `${meta.color}10` }}>
                        {meta.label}
                      </span>
                      {inv.item.type === "irl" && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full border border-cyan-500/30 text-cyan-400">Wird versendet</span>
                      )}
                    </div>
                  </div>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="#22c55e" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0" style={{ filter: "drop-shadow(0 0 6px rgba(34,197,94,0.6))" }}><polyline points="20 6 9 17 4 12"/></svg>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* Purchase modal */}
      <Modal open={!!selectedItem} onClose={() => setSelectedItem(null)} title={`${selectedItem?.emoji} ${selectedItem?.name}`}>
        {selectedItem && (() => {
          const canAfford = coins >= selectedItem.cost;
          const meta      = RARITY_META[selectedItem.rarity] || RARITY_META.common;
          const pCount    = purchasedCounts[selectedItem.id] || 0;
          const maxHit    = selectedItem.maxPerUser != null && pCount >= selectedItem.maxPerUser;
          return (
            <div className="space-y-4">
              {/* Item showcase */}
              <div
                className="text-center p-7 rounded-2xl border-2 relative overflow-hidden holographic"
                style={{ borderColor: meta.border, background: meta.bg }}
              >
                <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(ellipse at 50% 0%, ${meta.glow} 0%, transparent 60%)` }} />
                <div
                  className="text-7xl mb-3 inline-block animate-float"
                  style={{ filter: `drop-shadow(0 0 20px ${meta.glow})` }}
                >
                  {selectedItem.emoji}
                </div>
                <span
                  className="text-xs px-3 py-1 rounded-full border font-black inline-block"
                  style={{ color: meta.color, borderColor: `${meta.color}50`, background: `${meta.color}12` }}
                >
                  ✦ {meta.label}
                </span>
              </div>

              {selectedItem.description && (
                <p className="text-sm text-gray-400 leading-relaxed">{selectedItem.description}</p>
              )}

              <div className="grid grid-cols-2 gap-2">
                <div className="bg-nexus-s2 rounded-xl p-3 text-center border border-nexus-border">
                  <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wide mb-1">Preis</p>
                  <CoinAmount value={selectedItem.cost} className="text-base text-yellow-400 justify-center" />
                </div>
                <div className="bg-nexus-s2 rounded-xl p-3 text-center border border-nexus-border">
                  <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wide mb-1">Guthaben</p>
                  <CoinAmount value={coins} className={clsx("text-base justify-center", canAfford ? "text-green-400" : "text-red-400")} />
                </div>
              </div>

              {selectedItem.type === "irl" && (
                <div className="flex items-start gap-2.5 p-3 bg-cyan-500/5 border border-cyan-500/20 rounded-xl">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="#06b6d4" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  <p className="text-[11px] text-cyan-400">Dieses Item wird per Post an dich gesendet. Dein Lehrer fragt nach deiner Adresse.</p>
                </div>
              )}

              {!canAfford && (
                <div className="flex items-center gap-2 p-3 bg-red-900/15 border border-red-800/30 rounded-xl">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="#ef4444" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  <p className="text-[11px] text-red-400">Dir fehlen <strong>{(selectedItem.cost - coins).toLocaleString("de-DE")}</strong> Coins.</p>
                </div>
              )}

              {selectedItem.maxPerUser != null && (
                <div className="flex items-center gap-2 p-3 bg-nexus-s2 border border-nexus-border rounded-xl">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="#94a3b8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>
                  <p className="text-[11px] text-gray-400">
                    {selectedItem.maxPerUser === 1
                      ? "Dieses Item kann nur 1× erworben werden."
                      : `Max. ${selectedItem.maxPerUser}× pro Account. Du hast ${pCount}× erworben.`}
                  </p>
                </div>
              )}

              <Button full disabled={!canAfford || maxHit} loading={buy.isPending} onClick={() => buy.mutate(selectedItem.id)}>
                {maxHit ? "Limit erreicht" : canAfford ? "✦ Jetzt kaufen" : "Nicht genug Coins"}
              </Button>
            </div>
          );
        })()}
      </Modal>

      <CoinBurn open={burnOpen} onClose={() => setBurnOpen(false)} />
    </div>
  );
}
